/**
 * Dynamic variable placeholders — the single source of truth for `{{var}}` syntax.
 *
 * This logic used to be reimplemented ~26 times across apps/web, packages/trigger
 * and packages/mcp-server, with FOUR mutually incompatible regexes. The divergence
 * was not cosmetic: half the codebase recognised `{{ user_email }}` (with inner
 * whitespace) and half did not, so a picker could offer a variable that the
 * substitution step then failed to replace, leaving the raw `{{...}}` in the
 * output the customer sees.
 *
 * Everything that reads, tests or substitutes a placeholder must go through here.
 *
 * NOT covered: WhatsApp template parameters (`{{1}}`, `{{2}}`). Those are Meta's
 * positional syntax, a different domain with its own validation rules — see
 * lib/whatsapp/template-validation.ts.
 */

/**
 * Canonical placeholder pattern: `{{name}}`, tolerant of inner whitespace and
 * supporting dotted paths (`{{contact.email}}`).
 *
 * Always build a fresh instance — a shared `/g` regex carries `lastIndex` between
 * calls, which silently skips matches on the second use.
 */
export function dynamicVariableRegex(): RegExp {
  return /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;
}

/** Prefix reserved for variables ElevenLabs injects itself. */
export const SYSTEM_VARIABLE_PREFIX = "system__";

/** True for platform-provided variables (`system__caller_id`, …). */
export function isSystemVariable(name: string): boolean {
  return name.startsWith(SYSTEM_VARIABLE_PREFIX);
}

export interface ExtractVariablesOptions {
  /** Drop `system__*` variables from the result. Default: false. */
  excludeSystem?: boolean;
}

/**
 * Every distinct variable name referenced across `texts`, in first-seen order.
 * Non-string and empty entries are ignored, so callers can pass optional fields
 * directly without pre-filtering.
 */
export function extractVariables(
  texts: Array<string | null | undefined> | string | null | undefined,
  options: ExtractVariablesOptions = {},
): string[] {
  const sources = Array.isArray(texts) ? texts : [texts];
  const found = new Set<string>();

  for (const source of sources) {
    if (typeof source !== "string" || !source) continue;
    const regex = dynamicVariableRegex();
    let match: RegExpExecArray | null;
    while ((match = regex.exec(source)) !== null) {
      const name = match[1];
      if (!name) continue;
      if (options.excludeSystem && isSystemVariable(name)) continue;
      found.add(name);
    }
  }

  return Array.from(found);
}

/** True when `text` references at least one `{{variable}}`. */
export function hasAnyVariable(text: string | null | undefined): boolean {
  return typeof text === "string" && dynamicVariableRegex().test(text);
}

/**
 * True when `text` references `{{name}}` specifically, whitespace-tolerant and
 * case-insensitive on the variable name.
 */
export function hasVariable(
  text: string | null | undefined,
  name: string,
): boolean {
  if (typeof text !== "string" || !text) return false;
  return extractVariables(text).some(
    (found) => found.toLowerCase() === name.toLowerCase(),
  );
}

/**
 * Substitute every `{{variable}}` present in `context`.
 *
 * Whitespace-tolerant, unlike the per-key `new RegExp("\\{\\{" + key + "\\}\\}")`
 * loops this replaces: those left `{{ caller_name }}` untouched in calendar event
 * titles while the UI that suggested the variable accepted the spaced form.
 *
 * Variables missing from `context` are left as-is by default, so an unresolved
 * placeholder stays visible instead of silently becoming an empty string. Pass
 * `fallback` to substitute them (use `""` to blank them out).
 */
export function replaceVariables(
  text: string,
  context: Record<string, string | number | null | undefined>,
  options: { fallback?: string } = {},
): string {
  return text.replace(dynamicVariableRegex(), (whole, name: string) => {
    const value = context[name];
    if (value !== undefined && value !== null) return String(value);
    return options.fallback !== undefined ? options.fallback : whole;
  });
}

/**
 * Remove every `{{variable}}` from `text`, collapsing the whitespace they leave
 * behind. Used to test whether a message carries real content beyond its
 * placeholders.
 */
export function stripVariables(
  text: string,
  options: { keepSystem?: boolean } = {},
): string {
  return text
    .replace(dynamicVariableRegex(), (whole, name: string) =>
      options.keepSystem && isSystemVariable(name) ? whole : "",
    )
    .replace(/\s+/g, " ")
    .trim();
}

/** Strip the surrounding braces from `{{name}}`, returning the bare name. */
export function stripBraces(value: string): string {
  return value.replace(/\{\{|\}\}/g, "").trim();
}

/**
 * Le variabili dinamiche con cui una chiamata era partita, lette dalla sua riga
 * di `call_history`.
 *
 * Vivono in `additional_data.payload.metadata.dynamicVariables` — un percorso
 * profondo che tre produttori diversi del payload post-chiamata dovrebbero
 * scrivere identico (`call-back-handler` due volte e
 * `outbound-call-status-checker`), ed e' esattamente il genere di cosa che
 * diverge alla prima modifica. Sta qui una volta sola.
 *
 * Verificato in produzione il 10/09/2026: 119 chiamate su 120 hanno le variabili
 * in quel percorso, sia da campagna sia singole. Nessun altro percorso in uso.
 *
 * Restituisce SOLO stringhe: l'`additional_data` e' jsonb e ci puo' finire di
 * tutto, mentre a valle queste finiscono in `{{call.dynamicVariables.x}}`, che
 * si risolve come testo. Un valore non testuale sarebbe «[object Object]» in un
 * messaggio al cliente.
 *
 * `null` quando non ce ne sono: una chiave assente e un oggetto vuoto dicono
 * cose diverse a chi ramifica con `is_not_empty`.
 */
export function readCallDynamicVariables(
  additionalData: unknown,
): Record<string, string> | null {
  const payload = (additionalData as { payload?: unknown } | null)?.payload;
  const metadata = (payload as { metadata?: unknown } | null)?.metadata;
  const raw = (metadata as { dynamicVariables?: unknown } | null)
    ?.dynamicVariables;

  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;

  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === "string") out[key] = value;
    else if (typeof value === "number" || typeof value === "boolean") {
      out[key] = String(value);
    }
    // Oggetti, array e null si scartano: non hanno una forma testuale onesta.
  }
  return Object.keys(out).length > 0 ? out : null;
}
